/**
 * Sheet — reusable side-anchored drawer overlay.
 *
 * Extracts the right-drawer pattern previously inlined in
 * `routes/twin/batches.tsx:217-252` so multiple surfaces can reuse it:
 *   - BatchTraceView right-side drawer (existing — B0.45 refactors to use this)
 *   - EntityDetailDrawer in /app/twin (B0.44 — moves from bottom-card to right-side)
 *   - AddGatewayDialog (B0.45)
 *   - CreateDashboardDialog (B0.45)
 *   - AlarmRule editor modal (B0.45)
 *
 * Renders as a full-viewport fixed overlay with a click-to-close backdrop
 * and an aside anchored to the chosen side. ESC key closes. Width is
 * controlled via Tailwind utility (`max-w-md`, `max-w-3xl`, etc).
 */

import { useEffect, useRef, type ReactNode } from "react";
import { cn } from "./cn";

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  /** Side the sheet is anchored to. Default "right". */
  side?: "right" | "left";
  /**
   * Tailwind max-width class for the sheet. Default `max-w-md` (~28rem).
   * Use `max-w-3xl` for content-heavy sheets like BatchTraceView.
   */
  widthClass?: string;
  /** Optional ARIA label for the dialog region. */
  ariaLabel?: string;
  /**
   * A panel that stays usable beside the sheet (the consultant's, on the right edge: lib/copilot/panel-layout):
   * `className` ends the sheet and its dimmed backdrop short of it, and `within` selects the panel, so an Escape pressed
   * in it is the panel's, never the sheet's, whichever listener came first. The sheet is then not modal: no
   * aria-modal, and the panel takes clicks and keys.
   */
  beside?: { className: string; within: string };
  children: ReactNode;
}

/**
 * The sheet's layer: above the page and the consultant's panel (z-30), under a dialog opened over it (z-50) and the
 * selection chip (lib/copilot/panel-layout SELECTION_CHIP_LAYER), so words selected in a sheet can be asked about.
 */
export const SHEET_LAYER = "z-40";

/**
 * Whether a key press closes a sheet: Escape, unless a dialog opened over the sheet (ConfirmDialog) handled it first, or
 * it was pressed inside the panel beside the sheet (`within`), which handles its own, whichever listener the window
 * registered first. Pure: the target need only answer `closest`.
 */
export function sheetEscapeCloses(
  e: { key: string; defaultPrevented: boolean; target: unknown },
  within?: string,
): boolean {
  if (e.key !== "Escape" || e.defaultPrevented) return false;
  const target = e.target as { closest?: (selector: string) => unknown } | null;
  return !(within && typeof target?.closest === "function" && target.closest(within));
}

export function Sheet({
  open,
  onClose,
  side = "right",
  widthClass = "max-w-md",
  ariaLabel,
  beside,
  children,
}: SheetProps) {
  const panelRef = useRef<HTMLElement>(null);
  const within = beside?.within;

  // ESC closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (sheetEscapeCloses(e, within)) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, within]);

  // Keyboard users land in the sheet when it opens and go back to what opened
  // it when it closes (the row or button they were on).
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    panelRef.current?.focus({ preventScroll: true });
    return () => opener?.focus?.({ preventScroll: true });
  }, [open]);

  if (!open) return null;

  return (
    <div
      className={cn("fixed inset-0 flex", SHEET_LAYER, beside?.className)}
      role="dialog"
      aria-modal={beside ? undefined : "true"}
      aria-label={ariaLabel}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-on-surface/40 backdrop-blur-sm"
      />
      <aside
        ref={panelRef}
        tabIndex={-1}
        className={cn(
          "relative flex h-full w-full flex-col overflow-y-auto bg-surface shadow-2xl animate-fade-in focus:outline-none",
          widthClass,
          side === "right" ? "ml-auto" : "mr-auto",
        )}
      >
        {children}
      </aside>
    </div>
  );
}

/**
 * SheetHeader — convenience wrapper for the common header pattern
 * (title + optional subtitle + close button).
 */
export function SheetHeader({
  title,
  subtitle,
  onClose,
  actions,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  actions?: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-border p-5">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <h2 className="truncate text-sm font-semibold uppercase tracking-wide text-on-surface">
          {title}
        </h2>
        {subtitle && (
          <p className="truncate text-xs text-on-surface-muted">{subtitle}</p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {actions}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close sheet"
          className="rounded-md p-1.5 text-on-surface-muted hover:bg-surface-elevated hover:text-on-surface"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="size-4"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>
  );
}
